// client/ui/help/helptext.en-US.ts
// Help text registry - single source of truth for all help text

export type HelpKey =
  // File Upload & Loading
  | "input.fileUpload"
  | "input.rubricUpload"
  | "input.interviewUpload"
  | "input.pdfUpload"

  // Navigation & Views
  | "nav.interviewResults"
  | "nav.documentResults"
  | "nav.dashboard"
  | "nav.roadmap"
  | "tab.technical"
  | "tab.documentsAll"
  | "tab.dataRnD"
  | "tab.dataFlow"
  | "tab.aiAnalysis"
  | "tab.liveData"
  | "tab.ontology"
  | "tab.graph"
  | "tab.evidence"
  | "tab.utilities"

  // Analysis Controls
  | "btn.runAnalysis"
  | "btn.analyzeSelected"
  | "btn.analyzeHighPriority"
  | "btn.demoAnalysis"
  | "check.lenientAcceptance"
  | "check.selectAllDocs"
  | "field.primaryPillars"
  | "field.configProfile"

  // Export Functions
  | "btn.askFairAI"
  | "btn.exportExcel"
  | "btn.exportDocument"
  | "btn.exportInterview"
  | "btn.exportSummary"
  | "btn.exportOntology"
  | "btn.exportTTL"
  | "btn.exportJSONLD"
  | "btn.exportOWL"
  | "btn.exportSHACL"
  | "btn.exportAIResults"

  // Search & Filters
  | "search.technical"
  | "search.documents"
  | "search.dataRnD"
  | "search.help"
  | "filter.miId"
  | "filter.pillar"
  | "filter.category"
  | "filter.dataType"
  | "filter.platform"
  | "filter.highConfidenceOnly"

  // Document Analysis
  | "doc.selectForAnalysis"
  | "doc.pillarFilter"
  | "doc.categoryFilter"
  | "doc.progressBar"
  | "doc.resultsPanel"

  // Interview Analysis
  | "interview.uploadZone"
  | "interview.analyzeBtn"
  | "interview.syntheticDemo"
  | "interview.personaCoverage"
  | "interview.evidenceTrail"
  | "interview.kpiStrip"

  // AI Analysis
  | "ai.uploadFiles"
  | "ai.analyzeTranscripts"
  | "ai.evidenceAudit"
  | "ai.updateAssessment"

  // Graph & Visualization
  | "graph.zoomIn"
  | "graph.zoomOut"
  | "graph.zoomFit"
  | "graph.zoomControls"
  | "graph.filterNodes"
  | "graph.layoutControls"
  | "graph.dataFlow"
  | "graph.backToOverview"

  // Ontology Features
  | "ontology.viewMode"
  | "ontology.exportFormat"
  | "ontology.graphView"
  | "ontology.browserView"
  | "ontology.complianceBar"
  | "ontology.fairRules"
  | "ontology.classHierarchy"
  | "ontology.sparqlEditor"
  | "ontology.evidencePanel"
  | "ontology.pillarCompliance"

  // FAIR Rules
  | "rule.compliance"
  | "rule.findable"
  | "rule.accessible"
  | "rule.interoperable"
  | "rule.reusable"
  | "rule.details"

  // Live Checks
  | "livecheck.pidResolution"
  | "livecheck.licenseDetection"
  | "livecheck.schemaValidation"
  | "livecheck.ontologyTerms"
  | "livecheck.runProbes"

  // Evidence & Results
  | "evidence.filter.highOnly"
  | "evidence.filter.pillar"
  | "evidence.confidenceScore"
  | "evidence.sourceReference"
  | "evidence.trail"

  // Utilities & Settings
  | "util.helpHints"
  | "util.editMode"
  | "util.adminPanel"
  | "field.searchHelp"

  // Charts & Visualizations
  | "chart.subComponents"
  | "chart.maturityHeatmap"
  | "chart.pillarComparison"
  | "chart.pistoiaHeatmap"
  | "chart.roiAnalysis"
  | "chart.valueProgression"

  // General Controls
  | "btn.back"
  | "btn.refresh"
  | "btn.reset"
  | "status.overallScore"
  | "badge.versionInfo"
  | "badge.aiEnhanced"
  | "badge.campanaSchott";

export const HELP_TEXT: Record<HelpKey, string> = {
  // File Upload & Loading
  "input.fileUpload": "Upload Excel workbook (.xlsx/.xls) containing FAIR assessment data for analysis.",
  "input.rubricUpload": "Load the FAIR_Rubric_Advanced.xlsx template to populate analysis criteria and scoring rubric.",
  "input.interviewUpload": "Upload interview transcript files (.docx) for AI-powered FAIR evidence extraction and persona mapping.",
  "input.pdfUpload": "Upload PDF documents for text extraction and automated FAIR analysis.",

  // Navigation & Views
  "nav.interviewResults": "View AI-analyzed stakeholder insights with persona coverage maps and evidence-based scoring from interview transcripts.",
  "nav.documentResults": "Review detailed FAIR scoring across 16 sub-components with Pistoia alignment and evidence trails from document analysis.",
  "nav.dashboard": "Access comprehensive FAIR maturity dashboard with pillar scores, heatmaps, and evidence-based analytics.",
  "nav.roadmap": "Explore 4-phase implementation strategy with timeline projections, ROI estimates, and prioritized recommendations.",
  "tab.technical": "View technical landscape with platform scores (Data/Compute/Storage/Sharing) and Pistoia MI Assessment mappings.",
  "tab.documentsAll": "Browse all business documents with FAIR pillar categorization, priority levels, and AI analysis capabilities.",
  "tab.dataRnD": "Explore Data R&D artifacts by type (Platform/Experimental/Reference/Discovery) with MI and platform associations.",
  "tab.dataFlow": "Visualize data flow architecture with interactive graph showing platform connections and data movement patterns.",
  "tab.aiAnalysis": "Upload interview transcripts for AI-powered evidence extraction, persona detection, and automated FAIR scoring.",
  "tab.liveData": "Perform live checks on datasets including PID resolution, license detection, schema validation, and ontology term verification.",
  "tab.ontology": "View ontology bundle with 16 FAIR Rules (F1-R4), compliance percentages, and semantic knowledge graph visualization.",
  "tab.graph": "Interactive Cytoscape graph showing FAIR relationships between datasets, metadata, identifiers, and protocols with compliance status.",
  "tab.evidence": "Browse complete evidence trail with confidence scores (High/Medium/Low), source references, and pillar-specific filtering.",
  "tab.utilities": "Access administrative tools including help hint management, edit mode toggle, and system configuration panels.",

  // Analysis Controls
  "btn.runAnalysis": "Execute FAIR analysis on selected documents/datasets using current profile settings with v2.7 scoring algorithm (50% primary gate, Pistoia +0.5 bonus).",
  "btn.analyzeSelected": "Analyze only the documents you've manually selected from the grid. Selection count shown in button label.",
  "btn.analyzeHighPriority": "Automatically analyze the 18 documents marked as High Priority in the Documents ALL sheet.",
  "btn.demoAnalysis": "Run demonstration analysis with synthetic data to preview scoring, evidence trails, and Excel export formats.",
  "check.lenientAcceptance": "When enabled, accepts Medium confidence evidence in addition to High. When disabled, only High confidence evidence contributes to FAIR scores (strict mode).",
  "check.selectAllDocs": "Toggle selection for all visible documents in the current filtered view. Use with pillar/category filters to batch-select specific document types.",
  "field.primaryPillars": "Specify which FAIR pillars (F/A/I/R) are primary for this assessment. Primary pillars require ≥50% sub-component coverage to pass scoring gate.",
  "field.configProfile": "Select analysis configuration profile: strict (High only), lenient (High+Medium), or very-lenient (includes Low). Controls evidence acceptance thresholds.",

  // Export Functions
  "btn.askFairAI": "Open AI chat assistant to ask questions about FAIR compliance, get explanations, and receive guidance on improving your data management.",
  "btn.exportExcel": "Download complete Excel workbook with FAIR scores, evidence trails, and detailed analytics. Requires completed analysis.",
  "btn.exportDocument": "Export Document Analysis workbook (v2.5) with 7 tabs: Summary, Evidence Trail, F/A/I/R pillar details, and Run Log with algorithm provenance.",
  "btn.exportInterview": "Export Interview Report workbook (v1.0) with 4 tabs: Summary, Researcher Perspective, IT Perspective, and Recommendations based on transcript analysis.",
  "btn.exportSummary": "Export FAIR Summary workbook (v1.0) aggregating scores from all evidence sources with 3 tabs: Overview, Evidence Inventory, and Gate Status.",
  "btn.exportOntology": "Export ontology data in selected semantic format. Use format dropdown to choose TTL/JSON-LD/OWL/SHACL before exporting.",
  "btn.exportTTL": "Download ontology as Turtle (.ttl) with rdfs: prefix for RDF graph representation of FAIR relationships and compliance rules.",
  "btn.exportJSONLD": "Download ontology as JSON-LD (.jsonld) for linked data applications with standard @context and @graph structure.",
  "btn.exportOWL": "Download ontology as OWL/XML (.owl) for use with ontology editors like Protégé. Includes class hierarchies and property definitions.",
  "btn.exportSHACL": "Download SHACL shapes (.ttl) defining validation constraints for FAIR dataset compliance. Use with SHACL validator tools.",
  "btn.exportAIResults": "Export AI analysis results including extracted evidence, persona mappings, confidence scores, and FAIR pillar assignments as structured report.",

  // Search & Filters
  "search.technical": "Search Pistoia MI Assessment table by MI ID, name, or description. Searches across all technical landscape entries.",
  "search.documents": "Search Documents ALL by name, category, pillar, or content description. Real-time filtering of document grid.",
  "search.dataRnD": "Search Data R&D artifacts by name, type, platform, or associated MI IDs. Supports partial matches.",
  "search.help": "Search help topics by keyword, helpId, or description. Finds relevant help entries across all application features.",
  "filter.miId": "Filter items by Pistoia MI ID (Management Initiative identifier). Shows only entries associated with selected MI.",
  "filter.pillar": "Filter by FAIR pillar: Findable (F), Accessible (A), Interoperable (I), or Reusable (R). Select 'All Pillars' to clear filter.",
  "filter.category": "Filter documents by business category (e.g., Protocols, Reports, Metadata). Categories loaded from Documents ALL sheet.",
  "filter.dataType": "Filter Data R&D by artifact type: Platform, Experimental Context, Reference, or Discovery. Helps segment analysis by data source.",
  "filter.platform": "Filter by data platform name. Shows only artifacts associated with selected platform from Technical Landscape.",
  "filter.highConfidenceOnly": "When enabled, shows only evidence items with High confidence (≥0.85). Filters out Medium and Low confidence entries.",

  // Document Analysis
  "doc.selectForAnalysis": "Check this box to include document in batch analysis. Selected count shown in 'Analyze Selected' button.",
  "doc.pillarFilter": "Filter document grid by FAIR pillar to focus on Findable, Accessible, Interoperable, or Reusable evidence sources.",
  "doc.categoryFilter": "Filter documents by business category to analyze specific document types (e.g., only Protocols or only Metadata files).",
  "doc.progressBar": "Shows analysis progress as percentage complete. Status updates in real-time as documents are processed.",
  "doc.resultsPanel": "Displays analysis summary upon completion: document count, average FAIR score, overall confidence, and quick export access.",

  // Interview Analysis
  "interview.uploadZone": "Click to upload interview transcript files (.docx, .txt, .pdf). Supports multiple file selection for batch processing.",
  "interview.analyzeBtn": "Process uploaded transcripts with AI to extract FAIR evidence, detect personas, and generate confidence-scored assertions.",
  "interview.syntheticDemo": "Run demonstration with pre-generated synthetic interview data to preview persona coverage, evidence extraction, and scoring logic.",
  "interview.personaCoverage": "Shows which stakeholder personas (Researcher, IT Admin, Data Steward, etc.) contributed evidence for each FAIR pillar.",
  "interview.evidenceTrail": "Browse extracted evidence items with source transcript references, persona attribution, confidence scores, and pillar assignments.",
  "interview.kpiStrip": "Key metrics from interview analysis: total assertions, coverage by pillar, top personas, and confidence distribution.",

  // AI Analysis
  "ai.uploadFiles": "Drop or select interview transcript files for AI processing. Accepts .docx, .txt, and .pdf formats. Multiple files supported.",
  "ai.analyzeTranscripts": "Apply AI to extract FAIR evidence from transcripts using cue family lexicons and negation detection. Generates structured assertions.",
  "ai.evidenceAudit": "Review complete audit trail of AI-extracted evidence showing source reference, persona, extracted text, assigned pillar, and confidence level.",
  "ai.updateAssessment": "Merge AI-extracted evidence into main FAIR assessment, updating pillar scores and evidence trails with interview-based findings.",

  // Graph & Visualization
  "graph.zoomIn": "Zoom in to see more detail in graph nodes and labels. Use mouse wheel or pinch gesture as alternative.",
  "graph.zoomOut": "Zoom out to see overall graph structure and patterns. Useful for understanding high-level architecture.",
  "graph.zoomFit": "Fit entire graph to viewport bounds. Auto-centers and scales graph for optimal viewing.",
  "graph.zoomControls": "Pan graph by clicking and dragging background. Zoom with +/- buttons or mouse wheel. Double-click nodes to focus.",
  "graph.filterNodes": "Show/hide nodes by type (Dataset, Metadata, Identifier), pillar (F/A/I/R), or compliance status (Pass/Fail).",
  "graph.layoutControls": "Change graph layout algorithm: force-directed (organic), hierarchical (top-down), circular, or grid. Affects node positioning.",
  "graph.dataFlow": "Interactive visualization of data flow between platforms and systems. Click nodes to drill down into specific platform details.",
  "graph.backToOverview": "Return to main data flow overview after drilling down into specific platform or connection details.",

  // Ontology Features
  "ontology.viewMode": "Switch between Graph View (Cytoscape visualization), Browser View (class hierarchy tree), or SPARQL Editor (query interface).",
  "ontology.exportFormat": "Choose semantic export format: Turtle (human-readable), JSON-LD (linked data), OWL (ontology editors), or SHACL (validation rules).",
  "ontology.graphView": "Interactive Cytoscape graph of ontology bundle showing Dataset, Metadata, PID, License, Protocol nodes with FAIR Rule relationships.",
  "ontology.browserView": "Hierarchical tree view of ontology classes with expand/collapse navigation. Shows class counts and property summaries.",
  "ontology.complianceBar": "Visual indicator showing FAIR Rule compliance percentage for specific pillar or overall assessment. Green = passed, Red = failed.",
  "ontology.fairRules": "Catalog of 16 FAIR Rules (F1-R4) with descriptions, OWL/SHACL snippets, compliance status, and related evidence.",
  "ontology.classHierarchy": "Browse ontology class structure with parent-child relationships. Click classes to see individuals and properties.",
  "ontology.sparqlEditor": "Execute read-only SPARQL queries against ontology RDF store. Use SELECT queries only; write operations blocked for safety.",
  "ontology.evidencePanel": "Shows evidence supporting ontology node compliance. Links to source documents, lexicon cues, and confidence calculations.",
  "ontology.pillarCompliance": "Compliance percentage by FAIR pillar (F/A/I/R). Requires ≥50% primary pillar coverage to pass scoring gate.",

  // FAIR Rules
  "rule.compliance": "Indicates whether this FAIR Rule passed validation (green checkmark), failed (red X), or is not applicable (gray dash) for current dataset.",
  "rule.findable": "Rules F1-F4 covering Persistent Identifiers, Rich Metadata, Metadata Includes ID, and Indexed in Searchable Resource.",
  "rule.accessible": "Rules A1-A4 covering Retrievable by Standard Protocol, Metadata Persistence, Authentication Procedures, and Authorization.",
  "rule.interoperable": "Rules I1-I4 covering Knowledge Representation, FAIR Vocabularies, Qualified References, and Standard Formats.",
  "rule.reusable": "Rules R1-R4 covering Rich Provenance, Detailed Provenance, Domain Standards, and Clear Usage License.",
  "rule.details": "Click to expand full rule description including validation criteria, OWL/SHACL code snippets, and current compliance evidence.",

  // Live Checks
  "livecheck.pidResolution": "Validates that Persistent Identifiers (DOIs, accessions) resolve to accessible landing pages with HTTP 2xx status.",
  "livecheck.licenseDetection": "Extracts and validates machine-readable license metadata from repository records. Supports CC BY, CC0, MIT, Apache patterns.",
  "livecheck.schemaValidation": "Validates file format integrity and structure using JSON Schemas for CSV/TSV/BED/GTF/FASTA/Excel files.",
  "livecheck.ontologyTerms": "Verifies that sample-level terms (disease, organism, characteristics) map to standard ontologies via OLS/BioPortal lookups.",
  "livecheck.runProbes": "Execute live validation probes against dataset URLs. Checks PID resolution, license presence, schema conformance, and ontology term validity.",

  // Evidence & Results
  "evidence.filter.highOnly": "Show only evidence items with High confidence (>0.85) that passed acceptance criteria. Filters out Medium and Low confidence entries.",
  "evidence.filter.pillar": "Filter evidence trail by FAIR pillar to see only Findable, Accessible, Interoperable, or Reusable evidence items.",
  "evidence.confidenceScore": "Numerical confidence score (0-1) calculated from cue weights using function: 1 - exp(-Σ weights). Mapped to High/Medium/Low bands.",
  "evidence.sourceReference": "Reference to source document, interview transcript, or live check probe where this evidence was detected.",
  "evidence.trail": "Complete audit log of all evidence items with source, confidence, pillar assignment, and contribution to final FAIR scores.",

  // Utilities & Settings
  "util.helpHints": "Administrative panel for managing help text overrides at Global/Org/User scopes. Search, create, edit, and delete help entries.",
  "util.editMode": "Toggle edit mode to show pencil overlays on all controls with help text. Click pencils to open inline editor for quick help updates.",
  "util.adminPanel": "Access system administration features including help management, configuration profiles, and feature flag controls.",
  "field.searchHelp": "Search help hint registry by helpId (e.g., 'btn.runAnalysis'), text content, or scope. Real-time filtering of help entries.",

  // Charts & Visualizations
  "chart.subComponents": "Bar chart showing FAIR scores across 16 sub-components (F1-F4, A1-A4, I1-I4, R1-R4) with target comparison.",
  "chart.maturityHeatmap": "2D heatmap showing FAIR maturity levels across pillars and criteria. Color intensity indicates score: green=high, yellow=medium, red=low.",
  "chart.pillarComparison": "Radar/spider chart comparing current vs target scores for F/A/I/R pillars with gap analysis.",
  "chart.pistoiaHeatmap": "Heatmap of Pistoia MI Assessment scores across platforms and MI categories. Shows Data/Compute/Storage/Sharing dimensions.",
  "chart.roiAnalysis": "Cost-benefit analysis chart showing projected ROI for FAIR improvements by phase with investment timeline.",
  "chart.valueProgression": "Timeline visualization of value accumulation across 4 implementation phases with cumulative ROI and milestone markers.",

  // General Controls
  "btn.back": "Navigate back to previous view or overview screen. Preserves filter and selection state.",
  "btn.refresh": "Reload current view with latest data from server. Clears local cache and re-fetches analysis results.",
  "btn.reset": "Reset all filters, selections, and view settings to default state. Does not affect saved analysis results.",
  "status.overallScore": "Aggregate FAIR score (0-5 scale) calculated from weighted pillar scores with primary coverage gates applied.",
  "badge.versionInfo": "Current application version and analysis algorithm version. Click to view changelog and release notes.",
  "badge.aiEnhanced": "Indicates AI-powered features are enabled including interview analysis, evidence extraction, and persona detection.",
  "badge.campanaSchott": "Powered by Campana Schott. Click to learn more about DATA Compass development and support."
};

export const HELP_TEXT_VERSION = "2025.10.11";
